import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import FilterBar from "@/components/feature/FilterBar";
import EmptyState from "@/components/feature/EmptyState";
import { videos } from "@/mocks/videos";
import { filterAndSort, searchVideos, type VideoFilter } from "@/lib/videoUtils";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryKeyword = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(queryKeyword);
  const [filter, setFilter] = useState<VideoFilter>({ region: "全部", year: "全部", sort: "hot" });

  useEffect(() => {
    setKeyword(queryKeyword);
  }, [queryKeyword]);

  usePageMeta({
    title: queryKeyword ? `${queryKeyword} - 搜索结果 - 多多视频` : "搜索影视 - 多多视频",
    description: queryKeyword
      ? `多多视频站内搜索“${queryKeyword}”的结果列表，快速发现相关电影、电视剧与短视频。`
      : "多多视频站内搜索，输入关键词快速查找电影、电视剧与短视频资源。",
    keywords: "多多视频,影视搜索,电影搜索,电视剧搜索,短视频搜索",
  });

  const baseResults = useMemo(() => searchVideos(videos, queryKeyword), [queryKeyword]);
  const results = useMemo(() => filterAndSort(baseResults, filter), [baseResults, filter]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (q) {
      setSearchParams({ q });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="min-h-screen bg-background-950">
      <SiteNavbar />
      <main className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-secondary-400 md:text-sm">
          <Link to="/" className="hover:text-accent-400 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-base" />
          <span className="text-foreground-200">搜索</span>
        </nav>

        <h1 className="mt-5 font-heading text-2xl font-extrabold text-foreground-50 md:text-4xl">
          搜索影视资源
        </h1>
        <p className="mt-2 text-sm text-foreground-400">
          输入片名或关键词，快速找到想看的电影、电视剧与短视频。
        </p>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-secondary-400" />
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、电视剧、短视频标题"
              className="h-12 w-full rounded-md border border-background-700 bg-background-900 pl-11 pr-4 text-sm text-foreground-100 placeholder:text-secondary-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <button
            type="submit"
            className="flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-8 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-600 cursor-pointer"
          >
            <i className="ri-search-line text-lg" />
            搜索
          </button>
        </form>

        {queryKeyword && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-300">当前关键词：</span>
            <span className="rounded-md bg-accent-500/15 px-3 py-1 text-sm font-semibold text-accent-300">
              {queryKeyword}
            </span>
            <span className="text-sm text-foreground-400">
              共找到 <span className="font-semibold text-foreground-100">{results.length}</span> 个结果
            </span>
          </div>
        )}

        {queryKeyword && results.length > 0 && (
          <div className="mt-6">
            <FilterBar filter={filter} onChange={setFilter} compact />
            <FilterBar filter={filter} onChange={setFilter} />
          </div>
        )}

        <div className="mt-8">
          {!queryKeyword ? (
            <div className="rounded-lg border border-background-800 bg-background-900 p-8 text-center md:p-12">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-secondary-400">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-50">开始你的搜索</h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-foreground-400">
                试试搜索“深海”“悬疑”“美食”等关键词，发现更多精彩影视内容。
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                {["深海纪元", "悬疑", "美食", "科幻", "青春"].map((kw) => (
                  <button
                    key={kw}
                    type="button"
                    onClick={() => setSearchParams({ q: kw })}
                    className="whitespace-nowrap rounded-md bg-secondary-100/10 px-4 py-2 text-sm text-foreground-300 transition-colors hover:bg-secondary-100/20 hover:text-foreground-50 cursor-pointer"
                  >
                    {kw}
                  </button>
                ))}
              </div>
            </div>
          ) : results.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {results.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <EmptyState
              title={`没有找到与“${queryKeyword}”相关的内容`}
              description="换个关键词试试看，或者浏览下面的热门推荐，也许会发现意想不到的好作品。"
              recommendations={[...videos].sort((a, b) => b.rating - a.rating).slice(0, 4)}
            />
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}